/**
 * VÉRAULT Portal — Consultation Completion modal (Phase 1.4)
 *
 * When an advisor marks a consultation complete, this modal collects
 * notes + outcome + downstream actions in one calm step:
 *   - Outcome: converted / follow-up / future wish / not moving / ...
 *   - Notes (free text)
 *   - Follow-up date (optional)
 *   - Convert lead → client
 *   - Create future wish (description)
 *   - Create draft experience (title, only if client exists)
 *   - Assign next task (description + due via follow-up date)
 *
 * Posts to POST /api/consultations/{id}/complete which performs all
 * side-effects atomically + logs a single rich audit row.
 */
import React from "react";
import { X, CheckCircle2, Loader2 } from "lucide-react";
import { Surface, Heading, PortalButton, api } from "../../index";
import { toast } from "sonner";

const OUTCOMES = [
  { id: "converted_to_client",  label: "Converted to client" },
  { id: "follow_up_needed",     label: "Follow-up needed" },
  { id: "future_wish",          label: "Future wish" },
  { id: "create_proposal",      label: "Create proposal" },
  { id: "waiting_on_client",    label: "Waiting on client" },
  { id: "not_moving_forward",   label: "Not moving forward" },
];

export default function ConsultationCompleteModal({ consultation, onClose, onDone }) {
  const [outcome, setOutcome] = React.useState("follow_up_needed");
  const [notes, setNotes] = React.useState("");
  const [followUpDate, setFollowUpDate] = React.useState("");
  const [convertToClient, setConvertToClient] = React.useState(false);
  const [futureWish, setFutureWish] = React.useState("");
  const [experienceTitle, setExperienceTitle] = React.useState("");
  const [nextTask, setNextTask] = React.useState("");
  const [saving, setSaving] = React.useState(false);

  const hasClient = !!consultation?.client_id;

  // When outcome changes, surface sensible defaults.
  React.useEffect(() => {
    if (outcome === "converted_to_client") setConvertToClient(true);
    if (outcome === "future_wish" && !futureWish) setFutureWish(consultation?.notes || "");
  }, [outcome]); // eslint-disable-line

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/consultations/${consultation.id}/complete`, {
        outcome,
        notes: notes || null,
        follow_up_date: followUpDate || null,
        convert_to_client: convertToClient,
        create_future_wish: futureWish || null,
        create_experience: experienceTitle || null,
        assign_next_task: nextTask || null,
      });
      const eff = data.side_effects || {};
      const summary = [
        eff.converted_client_id && "client promoted",
        eff.future_wish_id && "future wish saved",
        eff.experience_id && "draft experience created",
        eff.task_id && "task assigned",
      ].filter(Boolean).join(" · ");
      toast.success("Consultation complete.", { description: summary || "Notes logged." });
      onDone?.(data);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not complete consultation.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="consultation-complete-modal"
    >
      <div className="bg-white w-full max-w-xl max-h-[90vh] overflow-hidden rounded-lg shadow-2xl flex flex-col">
        <div className="flex items-start justify-between p-5 border-b border-[rgba(0,0,0,0.06)]">
          <div className="min-w-0">
            <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--accent-gold))]">
              Mark complete
            </p>
            <Heading level="h3" className="mt-1">
              {consultation?.prospect_name || consultation?.prospect_email || "Consultation"}
            </Heading>
            <p className="mt-1 font-accent italic text-[12.5px] text-[hsl(var(--foreground-muted))]">
              {consultation?.start_at}
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))]" data-testid="consultation-complete-close">
            <X className="size-4" strokeWidth={1.5} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Outcome */}
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] mb-2">
              Outcome
            </label>
            <div className="grid grid-cols-2 gap-1.5" data-testid="consultation-complete-outcomes">
              {OUTCOMES.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => setOutcome(o.id)}
                  className={`px-3 py-2 rounded-md font-serif text-[13.5px] text-left border ${outcome === o.id ? "bg-[hsl(var(--foreground))] text-white border-[hsl(var(--foreground))]" : "bg-white border-[rgba(0,0,0,0.06)] text-[hsl(var(--foreground-secondary))] hover:border-[hsl(var(--accent-gold))]/40"}`}
                  data-testid={`consultation-complete-outcome-${o.id}`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] mb-1">
              Notes from this consultation
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="A few quiet lines — outcome, vibe, anything worth remembering."
              className="w-full px-3 py-2 bg-white border border-[rgba(0,0,0,0.1)] rounded-md font-serif text-[14px] outline-none focus:border-[hsl(var(--accent-gold))]/40 resize-none"
              data-testid="consultation-complete-notes"
            />
          </div>

          {/* Follow-up date */}
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] mb-1">
              Follow-up date (optional)
            </label>
            <input
              type="date"
              value={followUpDate}
              onChange={(e) => setFollowUpDate(e.target.value)}
              className="px-3 py-2 bg-white border border-[rgba(0,0,0,0.1)] rounded-md font-serif text-[14px] outline-none focus:border-[hsl(var(--accent-gold))]/40"
              data-testid="consultation-complete-followup-date"
            />
          </div>

          {/* Convert to client */}
          {consultation?.lead_id && (
            <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="consultation-complete-convert-toggle">
              <input
                type="checkbox"
                checked={convertToClient}
                onChange={(e) => setConvertToClient(e.target.checked)}
                className="size-3.5 rounded border-[rgba(0,0,0,0.2)] text-[hsl(var(--accent-gold))]"
              />
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
                Convert lead to client
              </span>
            </label>
          )}

          {/* Future wish */}
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] mb-1">
              Create a future wish (optional)
            </label>
            <input
              type="text"
              value={futureWish}
              onChange={(e) => setFutureWish(e.target.value)}
              placeholder="e.g. Anniversary trip to Greece, summer 2027"
              className="w-full px-3 py-2 bg-white border border-[rgba(0,0,0,0.1)] rounded-md font-serif text-[14px] outline-none focus:border-[hsl(var(--accent-gold))]/40"
              data-testid="consultation-complete-future-wish"
            />
          </div>

          {/* Create experience */}
          {hasClient && (
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] mb-1">
                Create a draft experience (optional)
              </label>
              <input
                type="text"
                value={experienceTitle}
                onChange={(e) => setExperienceTitle(e.target.value)}
                placeholder="Working title — saves as a draft trip"
                className="w-full px-3 py-2 bg-white border border-[rgba(0,0,0,0.1)] rounded-md font-serif text-[14px] outline-none focus:border-[hsl(var(--accent-gold))]/40"
                data-testid="consultation-complete-experience"
              />
            </div>
          )}

          {/* Next task */}
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] mb-1">
              Assign next task (optional)
            </label>
            <input
              type="text"
              value={nextTask}
              onChange={(e) => setNextTask(e.target.value)}
              placeholder="e.g. Send Portugal proposal by Friday"
              className="w-full px-3 py-2 bg-white border border-[rgba(0,0,0,0.1)] rounded-md font-serif text-[14px] outline-none focus:border-[hsl(var(--accent-gold))]/40"
              data-testid="consultation-complete-next-task"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 p-4 border-t border-[rgba(0,0,0,0.06)]">
          <PortalButton variant="ghost" size="sm" onClick={onClose} disabled={saving}>
            Cancel
          </PortalButton>
          <PortalButton
            variant="primary"
            size="sm"
            onClick={submit}
            disabled={saving}
            icon={saving ? <Loader2 className="size-3.5 animate-spin" strokeWidth={1.5} /> : <CheckCircle2 className="size-3.5" strokeWidth={1.5} />}
            data-testid="consultation-complete-confirm"
          >
            {saving ? "Saving…" : "Complete"}
          </PortalButton>
        </div>
      </div>
    </div>
  );
}
